"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, TABLE, TD, TH, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { QrStrings } from "@/components/tools/qr-code-generator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  blockStructure,
  makeSegments,
  numDataCodewords,
  type QrCode,
  type QrEcc,
  type QrMode,
  totalBits,
} from "@/lib/tool-kit/qr/qrcodegen";

/** What a finished QR code is made of: version, mode, level, capacity and masks. */

const MODE_LABELS: Record<QrMode, keyof QrStrings> = {
  numeric: "modeNumeric",
  alphanumeric: "modeAlphanumeric",
  byte: "modeByte",
};

function PenaltyTable({ qr, strings }: { qr: QrCode; strings: QrStrings }) {
  const fmt = useToolNumber();
  if (!qr.penalties) {
    return null;
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.penaltiesHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.penaltyMask}
              </th>
              <th className={TH} scope="col">
                {strings.penaltyScore}
              </th>
            </tr>
          </thead>
          <tbody>
            {qr.penalties.map((penalty, mask) => (
              <tr
                className={
                  mask === qr.mask ? "bg-primary/5 font-semibold" : undefined
                }
                // biome-ignore lint/suspicious/noArrayIndexKey: the index is the mask number
                key={mask}
              >
                <td className={TD}>
                  {fmt(mask)}
                  {mask === qr.mask ? (
                    <span className="ml-2 text-muted-foreground text-xs">
                      {strings.chosen}
                    </span>
                  ) : null}
                </td>
                <td className={`${TD} font-mono`}>{fmt(penalty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={HINT}>{strings.penaltiesHint}</p>
    </div>
  );
}

export function QrDetails({
  qr,
  text,
  chosenEcc,
  strings,
}: {
  qr: QrCode;
  text: string;
  chosenEcc: QrEcc;
  strings: QrStrings;
}) {
  const fmt = useToolNumber();
  const segments = makeSegments(text);
  const used = totalBits(segments, qr.version);
  const capacity = numDataCodewords(qr.version, qr.ecc) * 8;
  const structure = blockStructure(qr.version, qr.ecc);
  const mode = segments[0]?.mode ?? "byte";
  return (
    <div className="space-y-4">
      <h3 className={WIDGET_H3}>{strings.detailsHeading}</h3>
      <StatGrid>
        <StatCard
          label={strings.version}
          value={t(strings.versionValue, {
            version: fmt(qr.version),
            size: fmt(qr.size),
          })}
        />
        <StatCard label={strings.mode} value={strings[MODE_LABELS[mode]]} />
        <StatCard label={strings.eccUsed} value={qr.ecc} />
        <StatCard
          label={strings.dataUsed}
          value={t(strings.dataUsedValue, {
            used: fmt(used),
            capacity: fmt(capacity),
          })}
        />
        <StatCard
          label={strings.blocks}
          value={t(strings.blocksValue, {
            blocks: fmt(structure.blocks),
            ecc: fmt(structure.eccPerBlock),
          })}
        />
        <StatCard label={strings.maskUsed} value={fmt(qr.mask)} />
      </StatGrid>
      {qr.ecc === chosenEcc ? null : (
        <p className={HINT}>
          {t(strings.eccRaised, { chosen: chosenEcc, used: qr.ecc })}
        </p>
      )}
      <PenaltyTable qr={qr} strings={strings} />
    </div>
  );
}
